// src/components/Bot/Chppy.js
import React, { useState, useRef, useEffect } from 'react';
import './Chppy.css';
import botResponses from '../../config/botResponses.json';

const Chppy = ({ isVisible }) => {
  // --- Hooks (must always run, in same order) ---
  const [currentState, setCurrentState] = useState('idle');
  const [isActive, setIsActive] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [currentFrameIndex, setCurrentFrameIndex] = useState(104); // Start at frame 104
  const characterRef = useRef(null);
  const messagesEndRef = useRef(null);
  const frameBaseStart = 68; // First frame index in sequence
  const frameSequence = Array.from({length: 90}, (_, i) => frameBaseStart + i).concat(
    Array.from({length: 15}, (_, i) => 177 + i)
  ); // Frames 68-157 and 177-191

  // Always declare effects unconditionally
  useEffect(() => {
    // Track mouse movement for frame animation
    const handleMouseMove = (e) => {
      if (!characterRef.current || isActive) return;

      const rect = characterRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
      const normalizedAngle = (angle + Math.PI) / (2 * Math.PI);
      const frameIndex = Math.floor(normalizedAngle * frameSequence.length) % frameSequence.length;

      setCurrentFrameIndex(frameIndex);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isActive, frameSequence.length]);

  // Scroll messages into view
  useEffect(() => {
    // Only scroll if there are messages and element exists
    if (messages.length && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // If we shouldn't render UI, return null AFTER hooks are declared
  if (!isVisible) return null;

  // --- Helpers & handlers (no hooks inside these) ---
  const getBotResponse = (userMessage) => {
    const msg = (userMessage || '').toLowerCase();
    const { keywords, responses } = botResponses;

    for (const key in keywords) {
      if (keywords[key].some(keyword => msg.includes(keyword))) {
        return responses[key];
      }
    }

    return responses.default.replace('{userMessage}', userMessage);
  };

  const handleMouseEnter = () => {
    if (currentState === 'idle' && !isActive) setCurrentState('attention');
  };

  const handleMouseLeave = () => {
    if (currentState === 'attention' && !isActive) setCurrentState('idle');
  };

  const handleClick = () => {
    if (!isActive) {
      setIsActive(true);
      setCurrentState('happy');
      setMessages([
        { text: "Hi! I'm Chppy, your Rippluence assistant. How can I help you today?", isUser: false }
      ]);
    }
  };

  const handleSendMessage = () => {
    if (!inputText.trim()) return;
    const userMessage = { text: inputText.trim(), isUser: true };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');

    setTimeout(() => {
      const botResponse = { text: getBotResponse(inputText), isUser: false };
      setMessages(prev => [...prev, botResponse]);
    }, 800);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSendMessage();
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsActive(false);
    setCurrentState('idle');
    setMessages([]);
    setInputText('');
  };

  // --- Rendered UI (this runs only when isVisible is true) ---
  return (
    <div
      className="chppy-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={!isActive ? handleClick : undefined}
    >
      {isActive ? (
        <div className="speech-bubble chat-active" onClick={(e) => e.stopPropagation()}>
          <button className="close-chat-btn" onClick={handleClose}>×</button>

          <div className="chat-messages">
            {messages.map((message, idx) => (
              <div key={idx} className={`message ${message.isUser ? 'user-message' : 'bot-message'}`}>
                {message.text}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <input
              className="chat-input"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              autoFocus
            />
            <button className="send-btn" onClick={handleSendMessage}>Send</button>
          </div>
        </div>
      ) : (
        <div className="speech-bubble">
          <div className="bubble-tail" />
          Hi I'm Chppy, your Rippluence assistant. Click me to chat!
        </div>
      )}

      <img
        ref={characterRef}
        className="chppy-character"
        src={`${process.env.PUBLIC_URL}/chatbot_frames/frame_${String(frameSequence[currentFrameIndex]).padStart(5, '0')}.webp`}
        alt="Chppy Assistant"
      />
    </div>
  );
};

export default Chppy;
